import type { BoundingBox } from '@rock/types';
import { isQuarterTurn, normalizeRotation } from '@rock/util';
import { XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { type FC, type ReactElement, useCallback, useState } from 'react';

export type ViewerImage = {
    /** Full-resolution image URL to display in the viewer. */
    url: string;
    /** Accessible label / alt text. */
    alt?: string;
    /**
     * Render-time rotation in degrees, straight off the image doc. The stored bytes are never
     * re-encoded, so a photo the user straightened in the app looks sideways here unless we apply it.
     */
    rotation?: number;
    /** Natural, pre-rotation pixel size. Required to place {@link highlights}. */
    width?: number;
    height?: number;
    /**
     * Regions worth outlining — the model-number / serial-number boxes the scan flow recorded — in the
     * ORIGINAL un-rotated image's pixel space, so they ride along with the rotation.
     */
    highlights?: BoundingBox[];
};

/** Matches the `p-4` on the dialog content — the padding the image has to fit inside. */
const VIEWER_PADDING = '2rem';

/** The app's viewer paints highlights in translucent red; admin matches so the two read as one UI. */
const HIGHLIGHT_BORDER = 'rgba(255,0,0,0.5)';
const HIGHLIGHT_FILL = 'rgba(255,0,0,0.2)';

/**
 * A reusable full-screen, full-resolution image viewer built on Radix Dialog. Closes on ESC (Radix
 * built-in), on any mouse click (overlay or the image itself), or via the close button. Drive it
 * directly with `image`/`open`/`onOpenChange`, or use {@link useImageViewer} for local open state.
 */
export const ImageViewer: FC<{
    image: ViewerImage | undefined;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}> = ({ image, open, onOpenChange }) => {
    // A quarter turn swaps the image's visible bounding box, so the fit constraints swap with it:
    // cap the un-rotated image at the viewport's *other* axis and it lands inside after rotating.
    const rotationDeg = normalizeRotation(image?.rotation);
    const quarterTurned = isQuarterTurn(rotationDeg);
    const maxWidth = quarterTurned ? `calc(100vh - ${VIEWER_PADDING})` : '100%';
    const maxHeight = quarterTurned ? `calc(100vw - ${VIEWER_PADDING})` : '100%';

    // Highlights are stored in pixels, so without the image's natural size there's nothing to scale
    // them against — an older doc missing those fields simply shows no overlays.
    const imageWidth = image?.width ?? 0;
    const imageHeight = image?.height ?? 0;
    const highlights = imageWidth > 0 && imageHeight > 0 ? (image?.highlights ?? []) : [];

    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/90" />
                {/* Content fills the viewport and closes on click, so a click anywhere — including on
                    the image — dismisses the viewer. */}
                <DialogPrimitive.Content
                    onClick={() => onOpenChange(false)}
                    className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 fixed inset-0 z-50 flex items-center justify-center p-4 focus:outline-none"
                >
                    <DialogPrimitive.Title className="sr-only">Image viewer</DialogPrimitive.Title>
                    {image ? (
                        // The wrapper shrink-wraps the rendered `<img>`, which puts percentage-placed
                        // highlights in the un-rotated image's coordinate space at any display size —
                        // then one rotate on the wrapper carries the image and its overlays together.
                        <div
                            className="relative inline-block"
                            style={{ transform: rotationDeg ? `rotate(${rotationDeg}deg)` : undefined }}
                        >
                            <img
                                src={image.url}
                                alt={image.alt ?? 'Image'}
                                className="block object-contain"
                                style={{ maxWidth, maxHeight }}
                            />
                            {highlights.map((box) => (
                                <div
                                    key={`${box.x}-${box.y}-${box.width}-${box.height}`}
                                    className="pointer-events-none absolute border-2"
                                    style={{
                                        left: `${(box.x / imageWidth) * 100}%`,
                                        top: `${(box.y / imageHeight) * 100}%`,
                                        width: `${(box.width / imageWidth) * 100}%`,
                                        height: `${(box.height / imageHeight) * 100}%`,
                                        borderColor: HIGHLIGHT_BORDER,
                                        backgroundColor: HIGHLIGHT_FILL,
                                    }}
                                />
                            ))}
                        </div>
                    ) : null}
                    <DialogPrimitive.Close
                        aria-label="Close"
                        className="absolute top-4 right-4 rounded-full bg-black/50 p-2 text-white/80 transition-colors hover:text-white focus:outline-none"
                    >
                        <XIcon className="h-5 w-5" />
                    </DialogPrimitive.Close>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};

/**
 * Local-state sugar over {@link ImageViewer}: call `openImage(img)` from a click handler and render
 * the returned `imageViewer` element once. The element keeps a stable component identity (only its
 * props change), so open/close animations play correctly.
 */
export function useImageViewer(): { openImage: (image: ViewerImage) => void; imageViewer: ReactElement } {
    const [image, setImage] = useState<ViewerImage | undefined>(undefined);

    const openImage = useCallback((next: ViewerImage) => setImage(next), []);
    const handleOpenChange = useCallback((open: boolean) => {
        if (!open) setImage(undefined);
    }, []);

    return {
        openImage,
        imageViewer: <ImageViewer image={image} open={!!image} onOpenChange={handleOpenChange} />,
    };
}
